import {
  APICallState,
  ProfileInfoType,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {DateTime} from 'luxon';

import {BusinessInfo, PersonalInfo} from 'models/Profile';

import {AuthActions} from 'store/auth/auth.reducer';

export enum PROFILE_TABS {
  PROFILE = 'PROFILE',
  SECURITY = 'SECURITY',
  PAYMENTS = 'PAYMENTS',
  STATEMENTS = 'STATEMENTS',
  PREFERENCES = 'COMMUNICATIONS',
}

export enum PAYMENT_MODULE_STATE {
  DEFAULT = 'DEFAULT',
  IS_EDITING = 'IS_EDITING',
  IS_ADDING = 'IS_ADDING',
}

export type PaymentLastFour = string;

export interface PaymentUpdateActionType {
  success: PaymentLastFour;
  isInitialPayment: boolean;
}

export interface ProfileState {
  readonly profileTabs: string[];
  readonly updatedUserInfo: {
    companionNumber?: string;
  };
  readonly updateCompanionNumberState: APICallState;
  readonly updateProfileInfoState: APICallState;

  readonly isMFATokenValid: boolean;
  readonly lastGetProfileFullInfoTime?: string;
  readonly paymentModuleState: PAYMENT_MODULE_STATE;
  readonly breadSDKError?: boolean;
}

export const initialState: ProfileState = {
  profileTabs: Object.values(PROFILE_TABS),
  updatedUserInfo: {},

  updateCompanionNumberState: APICallState.INITIAL,
  updateProfileInfoState: APICallState.INITIAL,
  isMFATokenValid: false,
  paymentModuleState: PAYMENT_MODULE_STATE.DEFAULT,
  breadSDKError: false,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    updateCompanionNumber(state, _action: PayloadAction<string>) {
      state.updateCompanionNumberState = APICallState.LOADING;
    },
    updateCompanionNumberSuccess(state, action: PayloadAction<string>) {
      state.updateCompanionNumberState = APICallState.SUCCESS;
      state.updatedUserInfo.companionNumber = action.payload;
    },
    updateCompanionNumberFailure(state) {
      state.updateCompanionNumberState = APICallState.ERROR;
    },
    updateCompanionNumberInvalid(state) {
      state.updateCompanionNumberState = APICallState.INVALID_CODE;
    },
    resetCompanionNumberState(state) {
      state.updateCompanionNumberState = APICallState.INITIAL;
    },
    updatePaymentModuleState(
      state,
      action: PayloadAction<PAYMENT_MODULE_STATE>
    ) {
      state.paymentModuleState = action.payload;
    },
    updateProfileInfo(
      state,
      action: PayloadAction<PersonalInfo | BusinessInfo>
    ) {
      state.updateProfileInfoState = APICallState.LOADING;
    },
    updateProfileInfoSuccess(state) {
      state.updateProfileInfoState = APICallState.SUCCESS;
    },
    updateProfileInfoFailure(state) {
      state.updateProfileInfoState = APICallState.ERROR;
    },
    resetProfileInfoState(state) {
      state.updateProfileInfoState = APICallState.INITIAL;
    },
    setIsMFATokenValid(state, action: PayloadAction<boolean>) {
      state.isMFATokenValid = action.payload;
    },
    setBreadSDKError(state, action: PayloadAction<boolean>) {
      state.breadSDKError = action.payload;
    },
    getProfile(_, _action: PayloadAction<{infoType: ProfileInfoType}>) {},
    getProfileSuccess(state) {
      state.lastGetProfileFullInfoTime = DateTime.local().toISO();
    },
    clearLastGetProfileFullInfoTime(state) {
      state.lastGetProfileFullInfoTime = undefined;
    },
  },
  extraReducers: builder => {
    builder.addCase(AuthActions.logout.type, () => initialState);
  },
});

export const ProfileActions = profileSlice.actions;

const ProfileReducer = profileSlice.reducer;
export default ProfileReducer;
